import { useState } from 'react'
import { LoaderFunctionArgs, useLoaderData } from 'react-router-dom'

import { useIsAccountClosed } from '../../components/AppShell'
import { ProfessionRow, ProfessionalsIndex, fetchProfessionals } from '../../api/professionals'
import {
  ButtonAnchor,
  DataTable,
  ErrorList,
  PageHeading,
  Panel,
  PanelBody,
  TableColumn,
} from '../../components/common'
import { getConfig } from '../../lib/config'
import styles from './ProfessionalsIndexPage.module.scss'

const PAGE_LENGTH = 10

const COLUMNS: readonly TableColumn<ProfessionRow>[] = [
  { key: 'name', label: 'Nombre', render: (row) => <p className="default-text-color">{row.name}</p> },
  { key: 'email', label: 'Email', render: (row) => <p className="default-text-color">{row.email}</p> },
  { key: 'rut', label: 'Rut', render: (row) => <p className="default-text-color">{row.rut}</p> },
  { key: 'phone', label: 'Teléfono', render: (row) => <p className="default-text-color">{row.phone}</p> },
  { key: 'profession', label: 'Profesión', render: (row) => <p className="default-text-color">{row.profession_title}</p> },
  { key: 'schedule', label: '', render: (row) => <a href={row.schedule_path}>Asignar horario</a> },
]

function _getCountLine(total: number, max: number): string {
  return max === 1 ? `Hay ${total} de ${max} profesional creado` : `Hay ${total} de ${max} profesionales creados`
}

export function loader({ params }: LoaderFunctionArgs): Promise<ProfessionalsIndex> {
  return fetchProfessionals(Number(params.healthCenterId))
}

export default function ProfessionalsIndexPage() {
  const data = useLoaderData() as Awaited<ReturnType<typeof loader>>
  const closed = useIsAccountClosed()
  const config = getConfig()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)

  const term = search.trim().toLowerCase()
  const matched =
    term === ''
      ? data.professionals
      : data.professionals.filter((professional) =>
          [professional.name, professional.email, professional.rut, professional.phone, professional.profession_title]
            .join(' ')
            .toLowerCase()
            .includes(term),
        )

  const pageCount = Math.max(1, Math.ceil(matched.length / PAGE_LENGTH))
  const current = Math.min(page, pageCount - 1)
  const rows = matched.slice(current * PAGE_LENGTH, (current + 1) * PAGE_LENGTH)

  return (
    <div className="col-md-10 col-md-offset-1 no-padding-xs no-margin-xs">
      <ErrorList messages={config.flash.alert ? [config.flash.alert] : []} />
      <Panel className={styles.container}>
        <PanelBody>
          {data.max_professionals !== null && <span>{_getCountLine(data.professionals.length, data.max_professionals)}</span>}
          <a href={data.plan_path} className={styles.planLink}>
            (cambiar plan)
          </a>
          <PageHeading level={2} className="hidden-sm hidden-md hidden-lg">
            Profesionales
          </PageHeading>
          <DataTable
            id="professional-table"
            columns={COLUMNS}
            rows={rows}
            getRowKey={(row) => row.id}
            tableClassName="patients-table"
            search={search}
            onSearchChange={(value) => {
              setSearch(value)
              setPage(0)
            }}
            page={current}
            pageCount={pageCount}
            onPageChange={setPage}
            total={matched.length}
            from={matched.length === 0 ? 0 : current * PAGE_LENGTH + 1}
            to={current * PAGE_LENGTH + rows.length}
          >
            <PageHeading level={2} className={`hidden-xs ${styles.floatingHeading}`}>
              Profesionales
            </PageHeading>
          </DataTable>
          <div className={styles.actions}>
            {!closed && (
              <>
                <ButtonAnchor href={data.new_path}>Nuevo Profesional</ButtonAnchor>{' '}
              </>
            )}
            <ButtonAnchor href={data.agenda_path}>Ir a Agenda</ButtonAnchor>
          </div>
        </PanelBody>
      </Panel>
    </div>
  )
}
